<template>
  <div class="group-compare" :class="{ 'group-compare--fill': fill }">
    <div class="group-compare-rows">
      <div v-for="row in rows" :key="'GC' + row.company_group_id" class="group-compare-row" :title="rowTitle(row)">
        <div class="group-compare-label">{{ row.label }}</div>
        <div class="group-compare-track">
          <div class="group-compare-bar" :style="{ width: barWidth(row) + '%', background: 'rgba(' + rgb + ', ' + (row.value < average ? 0.5 : 0.95) + ')' }"></div>
          <div v-if="rows.length > 1" class="group-compare-average" :style="{ left: barWidth({ value: average }) + '%' }"></div>
        </div>
        <div class="group-compare-value">{{ format(row.value) }}</div>
      </div>
    </div>
    <div v-if="rows.length > 1" class="group-compare-legend">
      <span class="badge badge-default group-compare-badge" title="Mean of the group values shown, each group counted once">
        <span class="group-compare-legend-line"></span>Group average {{ format(average) }}
      </span>
    </div>
    <div v-if="rows.length === 0" class="group-compare-empty">No data for this period</div>
  </div>
</template>

<script>
export default {
  name: 'GroupComparisonChart',
  props: {
    // Rows from the by-group branch: { company_group_id, label, value }
    chartData: {
      type: Array,
      required: true,
    },
    // Formats a value the way the widget's data type demands (currency, percent, count).
    format: {
      type: Function,
      default: (v) => String(v),
    },
    fill: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: '',
    },
  },
  computed: {
    rgb() {
      // Widget highlight color as "r, g, b" for rgba() mixes; falls back to the brand primary.
      const hex = /^#[0-9a-fA-F]{6}$/.test(this.color || '') ? this.color : '#e14eca';
      return [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16)).join(', ');
    },
    rows() {
      return (this.chartData || [])
        .map(r => ({ company_group_id: r.company_group_id, label: r.label, value: Number(r.value) || 0 }))
        .sort((a, b) => b.value - a.value);
    },
    max() {
      return this.rows.reduce((m, r) => Math.max(m, r.value), 0);
    },
    average() {
      if (this.rows.length === 0) return 0;
      return this.rows.reduce((s, r) => s + r.value, 0) / this.rows.length;
    },
  },
  methods: {
    barWidth(row) {
      return this.max > 0 ? Math.max(1, (row.value / this.max) * 100) : 0;
    },
    rowTitle(row) {
      const diff = this.average ? Math.round(((row.value - this.average) / this.average) * 100) : 0;
      return row.label + ': ' + this.format(row.value) + (this.rows.length > 1 ? ' (' + (diff >= 0 ? '+' : '') + diff + '% vs group average)' : '');
    },
  },
};
</script>

<style scoped>
.group-compare {
  width: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.group-compare--fill {
  height: 100%;
}
.group-compare-rows {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
}
.group-compare-row {
  display: grid;
  grid-template-columns: minmax(90px, 30%) 1fr auto;
  align-items: center;
  gap: 10px;
  flex: 0 1 auto;
}
/* Rows grow into the tile up to a comfortable bar height. */
.group-compare--fill .group-compare-row {
  flex: 1 1 0;
  max-height: 44px;
  min-height: 24px;
}
.group-compare-label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.group-compare-track {
  position: relative;
  height: 100%;
  min-height: 20px;
  max-height: 30px;
  background: rgba(125, 125, 125, 0.12);
  border-radius: 5px;
}
.group-compare-bar {
  height: 100%;
  border-radius: 5px;
  transition: width 0.3s ease;
}
.group-compare-average {
  position: absolute;
  top: -4px;
  bottom: -4px;
  width: 3px;
  background: #ffffff;
  border-radius: 2px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.5);
}
.group-compare-value {
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
  white-space: nowrap;
  min-width: 60px;
  text-align: right;
}
.group-compare-legend {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 8px;
  font-size: 11px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
}
.group-compare-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: none;
  font-weight: 500;
}
.group-compare-legend-line {
  width: 3px;
  height: 12px;
  background: #ffffff;
  border-radius: 2px;
}
.group-compare-empty {
  text-align: center;
  color: rgba(125, 125, 125, 0.9);
  font-size: 12px;
}
.white-content .group-compare-label,
.white-content .group-compare-value {
  color: rgba(0, 0, 0, 0.8);
}
.white-content .group-compare-legend {
  color: rgba(0, 0, 0, 0.65);
}
.white-content .group-compare-average,
.white-content .group-compare-legend-line {
  background: #1e1e2f;
}
</style>
